import * as React from 'react';
import { muiForm } from '../../../../../helpers/forms';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomDomainStatus,
  useAddCustomDomainMutation,
  useConfirmDomainMutation,
  useRemoveCustomDomainMutation,
} from 'generated/graphql';
import {
  CustomDomainEntity,
  DomainFormType,
  domainForm,
} from './domainForm.schema';

const caaSetupRequired = ['hostinger'];

export const useDomainSetup = ({
  creatorId,
  customDomains,
  refetch,
}: {
  customDomains: CustomDomainEntity[];
  creatorId: number;
  refetch: () => void;
}) => {
  const [showConfirmDomainModal, setShowConfirmDomainModal] =
    React.useState(false);
  const { register, watch, handleSubmit, reset, setValue, control } = muiForm(
    useForm<DomainFormType>({
      defaultValues: {
        domain: '',
        repeatDomain: '',
        subdomain: '',
        hostingProvider: '',
      },
      resolver: zodResolver(domainForm),
    }),
  );

  const [domainEntity, setDomainEntity] = React.useState<CustomDomainEntity>(
    customDomains[0],
  );

  React.useEffect(() => {
    if (domainEntity?.domain) {
      const [h, ...t] = domainEntity.domain.split('.');
      setValue('domain', t.join('.'));
      setValue('repeatDomain', t.join('.'));
      setValue('subdomain', h);
    }
  }, [domainEntity, setValue]);

  const domain = watch('domain');
  const subdomain = watch('subdomain');
  const hostingProvider = watch('hostingProvider');

  const fullDomain = `${subdomain.trim()}.${domain.trim()}`;
  const appUrl = `https://${fullDomain}`;

  const [addCustomDomainMutation, { loading: addDomainLoading }] =
    useAddCustomDomainMutation({
      variables: {
        domain: fullDomain,
        influencerId: creatorId,
      },
    });

  const [confirmDomainMutation, { loading: confirmDomainLoading }] =
    useConfirmDomainMutation({
      variables: {
        domainId: Number(domainEntity?.id),
        influencerId: creatorId,
      },
    });

  const [removeCustomDomainMutation, { loading: removeDomainLoading }] =
    useRemoveCustomDomainMutation({
      variables: {
        domainId: Number(domainEntity?.id),
        influencerId: creatorId,
      },
    });

  const [errorMessage, setErrorMessage] = React.useState(
    domainEntity?.additionalInfo?.error?.message ?? '',
  );
  const STATUS_TO_STEP = {
    [CustomDomainStatus.SslIssued]: 1,
    [CustomDomainStatus.AwaitingDnsConfiguration]: 1,
    [CustomDomainStatus.InvalidDnsConfiguration]: 1,
    [CustomDomainStatus.DnsVerified]: 2,
    [CustomDomainStatus.InfraRunning]: 2,
    [CustomDomainStatus.Error]: 2,
    [CustomDomainStatus.Initialized]: 0,
  };
  const [activeStep, setActiveStep] = React.useState(
    STATUS_TO_STEP[domainEntity?.status] || 0,
  );
  const onSubmit = handleSubmit((_data) => {
    setErrorMessage('');
    addCustomDomainMutation()
      .then((res) => {
        if (!res.data) {
          throw new Error('Wrong response from the server');
        }
        setDomainEntity(res.data.addCustomDomain);
        setActiveStep(1);
      })
      .catch((e) => {
        setErrorMessage(e.message);
      });
  });

  const onConfirmDnsSetup = handleSubmit(() => {
    setErrorMessage('');
    confirmDomainMutation()
      .then((res) => {
        const status = res.data?.confirmDomain.status;
        if (status === CustomDomainStatus.DnsVerified) {
          setActiveStep(2);
        } else {
          setShowConfirmDomainModal(true);
        }
        refetch();
      })
      .catch((e) => {
        setErrorMessage(e.message);
        setShowConfirmDomainModal(true);
      });
  });

  const onRemoveDomain = () => {
    setErrorMessage('');
    removeCustomDomainMutation()
      .then(() => {
        reset();
        setActiveStep(0);
        refetch();
      })
      .catch((e) => {
        setErrorMessage(e.message);
      });
  };

  const hideConfirmDomainModal = () => setShowConfirmDomainModal(false);

  const showCaaTable =
    domainEntity?.additionalInfo?.errorCode === 'CAA_RECORDS_BLOCKS_ACM' ||
    caaSetupRequired.includes(hostingProvider);
  const configError = mapErrorCodeToUserError(
    domainEntity?.additionalInfo?.errorCode,
  );

  return {
    loading: {
      addDomainLoading,
      confirmDomainLoading,
      removeDomainLoading,
    },
    domain,
    subdomain,
    register,
    onSubmit,
    errorMessage,
    activeStep,
    setActiveStep,
    domainEntity,
    onConfirmDnsSetup,
    appUrl,
    onRemoveDomain,
    hostingProvider,
    hideConfirmDomainModal,
    showConfirmDomainModal,
    showCaaTable,
    configError,
    control,
  };
};

const mapErrorCodeToUserError = (errorCode?: string) => {
  if (!errorCode) {
    return;
  }
  switch (errorCode) {
    case 'DOMAIN_MISSING_CNAME_RECORD':
      return 'First row from CNAME table has not been set';
    case 'DOMAIN_BAD_CNAME_RECORD':
      return "First row from CNAME table has typo in provider's DNS table input";
    case 'CERT_MISSING_CNAME_RECORD':
      return 'Second row from CNAME table has not been set';
    case 'CERT_BAD_CNAME_RECORD':
      return "Second row from CNAME table has typo in provider's DNS table input";
    case 'CAA_RECORDS_BLOCKS_ACM':
      return 'Please set up CAA records from second table';
    default:
      return undefined;
  }
};
